import { z } from "zod";
import { BLOCK_KINDS } from "../blocks/schema.js";
import { ENGINES } from "../catalog-sidecar.js";
// A run's preflight, the shapes both answers share: the dev tools' (devtools.preflightProject)
// and the Builder's (runs.preflight), which adds what the run needs and the cap's bounds.
/**
 * A preflight: what starting a run would check, without starting one. WHITELISTED — every
 * field below is a code, an id, a count or a fixed sentence from V5's own code: never an
 * agent's instructions or prompts, a credential, an endpoint, an MCP url or a deployment's
 * connection details. Models are named by their public `provider/model` ids.
 */
export const PREFLIGHT_UNCHECKED = [
    "provider_acceptance",
    "provider_availability",
    "mcp_discovery",
];
export const PreflightFindingSchema = z.object({
    /** The checker's rule id (a feature such as `background_runs`, `mcp:<server>`), or a compile/plan rule. */
    rule: z.string(),
    severity: z.enum(["fail", "warn", "info"]),
    /** enforced = a start refuses on a fail; advisory = the engine does not run this check at start. */
    enforcement: z.enum(["enforced", "advisory"]),
    /** The agent the finding is about, by its name; absent for the overlay or the run as a whole. */
    agent: z.string().optional(),
    /**
     * The block the finding's field belongs to (design h4 §B.6, `@mfa/v5-contracts/blocks`
     * blockOfPointer): a block kind, or `run` for the run's expectations (`/runtime/…`), which no
     * block holds; absent when the field names none. The field itself never crosses.
     */
    block: z.enum([...BLOCK_KINDS, "run"]).optional(),
    /**
     * Which entry of that block: a builtin's name, an MCP server's name or a custom tool's name,
     * kept only when it is a plain identifier (as `rule` keeps a feature's subject).
     */
    blockKey: z.string().optional(),
});
export const PreflightAgentSchema = z.object({
    name: z.string(),
    model: z.string(),
    role: z.enum(["root", "member", "self", "advisor"]),
});
export const PreflightProfileSchema = z.object({
    model: z.string(),
    probedOn: z.string(),
    tier: z.enum(["full", "basic", "none"]).nullable(),
    /** Older than the checker's limit (45 days). */
    stale: z.boolean(),
});
export const PreflightUncheckedSchema = z.object({
    check: z.enum(PREFLIGHT_UNCHECKED),
    reason: z.string(),
});
export const PreflightResultSchema = z.object({
    /** The project's current revision: the one a start would pin. */
    revision: z.number().int().positive(),
    checkedAt: z.string().datetime(),
    engine: z.enum(ENGINES).nullable(),
    engineInstalled: z.boolean(),
    mission: z.object({ source: z.enum(["input", "goal", "missing"]) }),
    budget: z.object({ cents: z.number().int(), defaulted: z.boolean() }),
    /** No blockers: a start would get as far as the provider. */
    ready: z.boolean(),
    /** What a start would answer, in order (the first is its error), with the same params. */
    blockers: z.array(z.object({ code: z.string(), params: z.record(z.string(), z.unknown()).optional() })),
    findings: z.array(PreflightFindingSchema),
    agents: z.array(PreflightAgentSchema),
    profiles: z.array(PreflightProfileSchema),
    /** Compile warnings, as `<agent>: <code>`. */
    warnings: z.array(z.string()),
    /** What only a real start can tell. */
    unchecked: z.array(PreflightUncheckedSchema),
});
/** The checker's verdict on the engine the model line selects, as the spec reports read it. */
export const RUN_PREFLIGHT_VERDICTS = ["ok", "degraded", "blocked"];
/**
 * What a run needs from the deployment, read off the root's and every member's agent.yaml
 * (the Needs the checker and the engines read). MCP servers are named as agent.yaml names
 * them and by host: never their URLs, whose path or query may carry a key.
 */
export const RunNeedsSchema = z.object({
    /** web_search or web_fetch is on for some agent. */
    web: z.boolean(),
    /** bash is on for some agent: the run executes code in a sandbox. */
    runsCode: z.boolean(),
    /** The MCP servers an agent's toolset calls, by name. */
    connectors: z.array(z.string()),
    /** Skills by id. */
    skills: z.array(z.string()),
    /** The hosts those MCP servers live on. */
    mcpHosts: z.array(z.string()),
});
/**
 * The Builder's preflight (runs.preflight): the dev tools' answer for the project's current
 * revision, plus the checker's verdict, what the run needs, and the cap's bounds.
 */
export const RunPreflightSchema = PreflightResultSchema.extend({
    /**
     * blocked: the engine refuses at start (an enforced fail); degraded: it runs with warnings
     * (advisory fails count here); ok: nothing to say. Null when no engine's checks ran.
     */
    verdict: z.enum(RUN_PREFLIGHT_VERDICTS).nullable(),
    needs: RunNeedsSchema,
    /** In cents: the cap a start that names none takes, and the most a start may name. */
    cap: z.object({ defaultCents: z.number().int(), maxCents: z.number().int() }),
});
//# sourceMappingURL=preflight.js.map